/**
 * Media Edit Component
 * Unified media uploader with drag-drop and icon-only layouts
 */

import * as React from 'react';
import {
    Button,
    Input,
    cn,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from '@ai-content-hub/content-kit';
import {
    uploadMedia,
    type UploadProgress,
    resolveMediaUrlForViewer,
    shouldResolveMediaUrlForViewer,
} from '@ai-content-hub/content-core';
import type { ElementRuntimeConfigSlice } from '@ai-content-hub/content-kit';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { Image, Volume2, Video, Upload, Link, Loader2, X, FileUp } from 'lucide-react';
import type {
    MediaElementData,
    MediaType,
    MediaDisplayConfig,
} from './schema';
import {
    DEFAULT_ACCEPTED_TYPES,
    DEFAULT_MEDIA_DISPLAY_CONFIG,
    getDisplayStyles,
    mediaElementSchema,
} from './schema';
import { useMediaUploadLimits } from './useMediaUploadLimits';

/** Layout types for media editor */
export type MediaEditLayout = 'default' | 'icon';

/** Icons for each media type */
const mediaIcons: Record<MediaType, typeof Image> = {
    image: Image,
    audio: Volume2,
    video: Video,
};

export interface MediaEditProps {
    /** Current media data */
    value: Partial<MediaElementData>;
    /** Callback when media changes */
    onChange: (data: MediaElementData) => void;
    /** Media type to accept */
    mediaType: MediaType;
    /** Layout mode: 'default' (drag-drop) or 'icon' (button only) */
    layout?: MediaEditLayout;
    /** Display config for preview */
    displayConfig?: MediaDisplayConfig;
    /** Whether the editor is disabled */
    disabled?: boolean;
    /** Additional class names */
    className?: string;
    /** Placeholder text */
    placeholder?: string;
    /** Host config (i18n) from element / content-type editor */
    config?: ElementRuntimeConfigSlice;
    /** Override initial locale */
    language?: string;
}

export const MediaEdit: React.FC<MediaEditProps> = ({
    value,
    onChange,
    mediaType,
    layout = 'default',
    displayConfig = DEFAULT_MEDIA_DISPLAY_CONFIG,
    disabled = false,
    className,
    placeholder,
    config,
    language,
}) => {
    const { t } = useElementSchemaLocale(mediaElementSchema.i18n, { config, language });

    const dropDefault =
        mediaType === 'image'
            ? t('dropZoneImage')
            : mediaType === 'audio'
              ? t('dropZoneAudio')
              : t('dropZoneVideo');

    const urlPlaceholder =
        mediaType === 'image'
            ? t('enterImageUrl')
            : mediaType === 'audio'
              ? t('enterAudioUrl')
              : t('enterVideoUrl');

    // State
    const [isDragging, setIsDragging] = React.useState(false);
    const [showLinkInput, setShowLinkInput] = React.useState(false);
    const [linkValue, setLinkValue] = React.useState('');
    const [isUploading, setIsUploading] = React.useState(false);
    const [uploadProgress, setUploadProgress] = React.useState(0);

    // Refs
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const dropZoneRef = React.useRef<HTMLDivElement>(null);

    // Get icon component
    const Icon = mediaIcons[mediaType];
    // EAA-1212: cap and accept list follow the server's configured media limits.
    const { maxSize, accept: acceptString } = useMediaUploadLimits(mediaType);
    const acceptedTypes = DEFAULT_ACCEPTED_TYPES[mediaType];

    // Current src
    const currentSrc = (value?.src || '').trim();

    const [resolvedPreviewSrc, setResolvedPreviewSrc] = React.useState<string | null>(() =>
        currentSrc && !shouldResolveMediaUrlForViewer(currentSrc) ? currentSrc : null,
    );
    const [resolvingPreview, setResolvingPreview] = React.useState(() =>
        Boolean(currentSrc && shouldResolveMediaUrlForViewer(currentSrc)),
    );

    React.useEffect(() => {
        const s = (value?.src || '').trim();
        if (!s || typeof window === 'undefined') {
            setResolvedPreviewSrc(null);
            setResolvingPreview(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(s)) {
            setResolvedPreviewSrc(s);
            setResolvingPreview(false);
            return;
        }
        let cancelled = false;
        setResolvingPreview(true);
        setResolvedPreviewSrc(null);
        void (async () => {
            try {
                const uri = await resolveMediaUrlForViewer(s);
                if (!cancelled) {
                    setResolvedPreviewSrc(uri || s);
                }
            } catch {
                if (!cancelled) {
                    setResolvedPreviewSrc(s);
                }
            } finally {
                if (!cancelled) {
                    setResolvingPreview(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [value?.src]);

    // Handle file upload
    const handleUpload = async (file: File) => {
        if (disabled) return;

        // Check file size
        if (file.size > maxSize) {
            alert(
                t('fileSizeExceedsMB').replace(
                    '{mb}',
                    String(Math.round(maxSize / 1024 / 1024))
                )
            );
            return;
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const result = await uploadMedia(
                file,
                { maxSize, acceptedTypes },
                {
                    onProgress: (progress: UploadProgress) => {
                        setUploadProgress(progress.percent);
                    },
                }
            );

            if (result.success && result.url) {
                onChange({
                    src: result.url,
                    mediaType,
                    title: value?.title || file.name,
                    displayConfig: value?.displayConfig || displayConfig,
                });
            } else {
                console.error('[MediaEdit] Upload failed:', result.error);
                alert(result.error || t('uploadFailedShort'));
            }
        } catch (error) {
            console.error('[MediaEdit] Upload error:', error);
            alert(
                t('uploadFailedShort') +
                    ': ' +
                    (error instanceof Error ? error.message : t('error', 'Unknown error'))
            );
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    // Handle file input change
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleUpload(file);
        }
        e.target.value = '';
    };

    // Handle drag events
    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget === dropZoneRef.current) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled) return;

        const file = e.dataTransfer.files?.[0];
        if (file) {
            // Validate file type
            const isValidType = acceptedTypes.some((type) => {
                if (type.endsWith('/*')) {
                    return file.type.startsWith(type.replace('/*', ''));
                }
                return file.type === type;
            });

            if (!isValidType) {
                alert(t('invalidFileTypeFor').replace('{type}', mediaType));
                return;
            }

            handleUpload(file);
        }
    };

    // Handle link submit
    const handleLinkSubmit = () => {
        const url = linkValue.trim();
        if (url) {
            onChange({
                src: url,
                mediaType,
                title: value?.title || '',
                displayConfig: value?.displayConfig || displayConfig,
            });
        }
        setShowLinkInput(false);
        setLinkValue('');
    };

    // Handle link cancel
    const handleLinkCancel = () => {
        setShowLinkInput(false);
        setLinkValue('');
    };

    // Handle remove media
    const handleRemove = () => {
        onChange({
            src: '',
            mediaType,
            title: '',
            displayConfig: value?.displayConfig || displayConfig,
        });
    };

    // Render preview
    const renderPreview = () => {
        if (!currentSrc) return null;

        if (
            shouldResolveMediaUrlForViewer(currentSrc) &&
            (resolvingPreview || !resolvedPreviewSrc)
        ) {
            return (
                <div className="flex items-center justify-center p-8 min-h-[120px]">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            );
        }

        const previewSrc = resolvedPreviewSrc ?? currentSrc;

        const styles = getDisplayStyles(displayConfig);

        switch (mediaType) {
            case 'image':
                return (
                    <img
                        src={previewSrc}
                        alt={value?.title || ''}
                        style={styles}
                        className="rounded"
                    />
                );
            case 'audio':
                return (
                    <audio src={previewSrc} controls className="w-full" style={styles} />
                );
            case 'video':
                return (
                    <video src={previewSrc} controls style={styles} className="rounded" />
                );
            default:
                return null;
        }
    };

    // Render upload progress
    const renderProgress = () => (
        <div className={cn('media-edit-progress', 'flex items-center gap-2 text-sm text-muted-foreground')}>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>
                {t('loading')} {uploadProgress}%
            </span>
            <div className={cn('media-edit-progress-bar-wrap', 'flex-1 h-1.5 bg-muted rounded-full overflow-hidden')}>
                <div
                    className={cn('media-edit-progress-bar', 'h-full bg-primary transition-all duration-200')}
                    style={{ width: `${uploadProgress}%` }}
                />
            </div>
        </div>
    );

    // Render link input
    const renderLinkInput = () => (
        <div className={cn('media-edit-link-input-wrap', 'flex items-center gap-2')}>
            <Input
                type="url"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
                placeholder={urlPlaceholder}
                className={cn('media-edit-link-input', 'flex-1 h-8 text-sm')}
                autoFocus
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLinkSubmit();
                    else if (e.key === 'Escape') handleLinkCancel();
                }}
            />
            <Button
                type="button"
                size="sm"
                onClick={handleLinkSubmit}
                disabled={!linkValue.trim()}
                className="h-8 px-3"
            >
                {t('ok')}
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleLinkCancel}
                className="h-8 px-2"
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );

    // Icon layout
    if (layout === 'icon') {
        return (
            <div className={cn('media-edit media-edit--icon', className)}>
                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    className={cn('media-edit-file-input', 'hidden')}
                    accept={acceptString}
                    onChange={handleFileChange}
                />

                {/* Link input */}
                {showLinkInput && <div className="mb-2">{renderLinkInput()}</div>}

                {/* Upload progress */}
                {isUploading && <div className="mb-2">{renderProgress()}</div>}

                {/* Icon button with dropdown */}
                {!showLinkInput && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type="button"
                                variant={currentSrc ? 'default' : 'outline'}
                                size="sm"
                                disabled={disabled || isUploading}
                                className={cn('media-edit-trigger-button', 'h-8 px-2')}
                                title={mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}
                            >
                                {isUploading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Icon className="h-4 w-4" />
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className={cn('media-edit-dropdown', 'min-w-[120px]')}>
                            <DropdownMenuItem
                                onClick={() => fileInputRef.current?.click()}
                                className={cn('media-edit-dropdown-item media-edit-dropdown-item--upload', 'cursor-pointer')}
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {t('upload')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => {
                                    setShowLinkInput(true);
                                    setLinkValue('');
                                }}
                                className={cn('media-edit-dropdown-item media-edit-dropdown-item--link', 'cursor-pointer')}
                            >
                                <Link className="h-4 w-4 mr-2" />
                                {t('fromLink')}
                            </DropdownMenuItem>
                            {currentSrc && (
                                <DropdownMenuItem
                                    onClick={handleRemove}
                                    className={cn('media-edit-dropdown-item media-edit-dropdown-item--remove', 'cursor-pointer text-destructive')}
                                >
                                    <X className="h-4 w-4 mr-2" />
                                    {t('removeMedia')}
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
        );
    }

    // Default layout (drag-drop)
    return (
        <div className={cn('media-edit media-edit--default space-y-3', className)}>
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                className={cn('media-edit-file-input', 'hidden')}
                accept={acceptString}
                onChange={handleFileChange}
                disabled={disabled}
            />

            {/* Link input mode */}
            {showLinkInput ? (
                renderLinkInput()
            ) : isUploading ? (
                renderProgress()
            ) : currentSrc ? (
                /* Preview with actions */
                <div className={cn('media-edit-preview-wrap', 'space-y-2')}>
                    <div className={cn('media-edit-preview', 'relative rounded-lg border bg-muted/30 p-2 flex items-center justify-center')}>
                        {renderPreview()}
                        {!disabled && (
                            <div className="absolute top-2 right-2 flex gap-1">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    className={cn('media-edit-replace-button', 'h-7 px-2')}
                                >
                                    <Upload className="h-3 w-3 mr-1" />
                                    {t('replaceMedia')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleRemove}
                                    className={cn('media-edit-remove-button', 'h-7 px-2')}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* Drop zone */
                <div
                    ref={dropZoneRef}
                    className={cn(
                        'media-edit-drop-zone',
                        isDragging && 'media-edit-drop-zone--dragging',
                        disabled && 'media-edit-drop-zone--disabled',
                        'relative rounded-lg border-2 border-dashed transition-colors',
                        'flex flex-col items-center justify-center gap-3 p-6',
                        isDragging
                            ? 'border-primary bg-primary/5'
                            : 'border-muted-foreground/25 hover:border-primary/50',
                        disabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <div className={cn('media-edit-drop-zone-icon', 'flex items-center justify-center w-12 h-12 rounded-full bg-muted')}>
                        <FileUp className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className={cn('media-edit-drop-zone-text', 'text-center')}>
                        <p className="text-sm font-medium">
                            {placeholder || dropDefault}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('maxSizeLabel')} {Math.round(maxSize / 1024 / 1024)}MB
                        </p>
                    </div>
                    <div className={cn('media-edit-drop-zone-actions', 'flex gap-2')}>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled}
                            className="media-edit-drop-zone-upload-button"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            {t('upload')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setShowLinkInput(true);
                                setLinkValue('');
                            }}
                            disabled={disabled}
                            className="media-edit-drop-zone-link-button"
                        >
                            <Link className="h-4 w-4 mr-2" />
                            {t('fromLink')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaEdit;

